/**
 * Per-field wrapper around FormAsyncSelect that resolves a bare scalar code
 * into a {value, label} option asynchronously via /client/options/ — without
 * re-rendering the parent form.
 *
 * Use when the stored value can be either a scalar id (e.g. set by an upstream
 * extractor with no access to the option label) or an already resolved
 * {value, label} option. The wrapper renders the bare code as a fallback label
 * while the lookup is in flight, then swaps in the description once
 * /client/options/ resolves. Cache-aware: a warm cache resolves synchronously
 * and the wrapper never flickers.
 */
import React, { useEffect, useState } from 'react';
import FormAsyncSelect from './form-async-select';
import { cachedFetch } from '../utils/cached-api-wrapper';

const EMPTY_FILTERS = encodeURIComponent('[]');

// Treats 0 / false as scalars — current callers store string ids only, so this is intentional.
const isScalarValue = (v) => v != null && v !== '' && typeof v !== 'object';

function toDisplayedOption(value, resolved) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') {
    return { value: value.value, label: value.label ?? value.value };
  }
  if (resolved && resolved.value === value) return resolved;
  return { value, label: value };
}

export default function LabelizedAsyncSelect({ fieldName, value, ...selectProps }) {
  const [resolved, setResolved] = useState(null);

  useEffect(() => {
    if (!isScalarValue(value)) {
      setResolved(null);
      return undefined;
    }
    let cancelled = false;
    cachedFetch(
      `/client/options/?field_name=${fieldName}&filters=${EMPTY_FILTERS}`,
      'GET',
      {},
      (response) => {
        if (cancelled) return;
        const row = (response?.data ?? []).find((r) => r.Id === value);
        setResolved(row ? { value: row.Id, label: row.Description } : { value, label: value });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [fieldName, value]);

  return <FormAsyncSelect {...selectProps} value={toDisplayedOption(value, resolved)} />;
}
